import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { TransitionKind } from '../../services/studioService';

const TRANSITIONS: Array<[TransitionKind, string]> = [['cut', 'Cut'], ['fade', 'Fade'], ['dissolve', 'Dissolve']];
/** A plain cut needs no label — it is the default and the arrow already says "then". */
const label = (t?: string): string | null => (t === 'fade' ? 'Fade' : t === 'dissolve' ? 'Dissolve' : null);

/**
 * SEAM — the join between two shots, and the only place a transition is set.
 *
 * It sits ON the board between the cards rather than in a side panel, because a transition is a property of
 * the JOIN, not of either shot. `transition` is stored on the FOLLOWING shot (the transition INTO it), which
 * is why this takes the index of the shot on the right.
 */
const SeamButton: React.FC<{
  /** Index of the shot the transition leads INTO. */
  intoIndex: number;
  current?: TransitionKind;
  onChange: (t: TransitionKind) => void;
}> = ({ intoIndex, current, onChange }) => {
  const [open, setOpen] = useState(false);
  const name = label(current);

  return (
    <div className="relative flex flex-col items-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Transition into shot ${intoIndex + 1}${name ? ` — currently ${name}` : ''}`}
        data-testid={`seam-${intoIndex}`}
        className="flex flex-col items-center gap-0.5 text-secondary-500 hover:text-secondary-800 dark:text-secondary-500 dark:hover:text-secondary-200"
      >
        <ArrowRight className="h-4 w-4" />
        {/* 10px is the Studio's readable floor (src/constants/typeScale) — this label was 8px. */}
        {name && <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">{name}</span>}
      </button>

      {open && (
        <div className="absolute top-full z-10 mt-1 flex flex-col gap-0.5 rounded-lg border border-secondary-200
                        bg-white p-1 shadow-lg dark:border-secondary-700 dark:bg-secondary-900">
          {TRANSITIONS.map(([key, text]) => {
            const on = (current || 'cut') === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { onChange(key); setOpen(false); }}
                className={`whitespace-nowrap rounded px-2 py-1 text-[11px] ${
                  on ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-700 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800'}`}
              >
                {text}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SeamButton;
